import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ENV } from "@app/constants/env";
import { getToken } from '@app/helpers/authProvider';
import { BaseType } from '@feature/types/basetype';

import { mstrchargecodeentity } from '@feature/helpers/GridColumnsChargeCode';
import { mstrcompanyentity } from '@feature/helpers/GridColumnsCompany';
import { mstrdepartmententity } from '@feature/helpers/GridColumnsDepartment';
import { mstrlocationentity } from '@feature/helpers/GridColumsLocation';
import { mstrshowprefixentity } from '@feature/helpers/GridColumnsShowPreFix';
import { mstrtransportcodeentity } from '@feature/helpers/GridColumnsTransportCode';
import { mstrvattaxentity } from '@feature/helpers/GridColumnsVatTax';
import { DmsAppConfigType, EmailAppConfigType } from '@feature/types/appconfigsettings.type';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.MASTERS_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken(); // ensure this returns a non-empty string
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set('Authorization', `Bearer ${token}`); // use standard casing
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    // optional: retry once if unauthorized (adjust getToken refresh logic if available)
    if (result.error && result.error.status === 401 && token) {
      // If you have a refresh flow, invoke it here then retry.
      // const refreshed = await getRefreshedToken();
      // if (refreshed) { headers.set('Authorization', `Bearer ${refreshed}`); return rawBaseQuery(args, api, extraOptions); }
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  error?: string
}



export const mastersapi = createApi({
  reducerPath: 'mastersapi',
  baseQuery: authBaseQuery,
  tagTypes: ['MastersLookups'],
  endpoints: (builder) => ({

    getallchargecodes: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'ChargeCode/GetAllChargeCodes',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),

    createchargecode: builder.mutation<ApiResponse<mstrchargecodeentity>, mstrchargecodeentity>({
      query: (mstrchargecodeentity) => ({
        url: 'ChargeCode/CreateChargeCode',
        method: 'POST',
        body: mstrchargecodeentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updatechargecode: builder.mutation<ApiResponse<mstrchargecodeentity>, mstrchargecodeentity>({
      query: (mstrchargecodeentity) => ({
        url: `ChargeCode/UpdateChargeCode/${mstrchargecodeentity.mstr_ChargeCode_ID}`,
        method: 'PUT',
        body: mstrchargecodeentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),

    getallcompany: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'Company/GetAllCompanies',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    createmstrcomapany: builder.mutation<ApiResponse<mstrcompanyentity>, mstrcompanyentity>({
      query: (mstrchargecodeentity) => ({
        url: 'Company/CreateCompany',
        method: 'POST',
        body: mstrchargecodeentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updatemstrcomapany: builder.mutation<ApiResponse<mstrcompanyentity>, mstrcompanyentity>({
      query: (mstrcompanyentity) => ({
        url: `Company/UpdateCompany/${mstrcompanyentity.mstr_CompanyID}`,
        method: 'PUT',
        body: mstrcompanyentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),



    getalldepartment: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'Department/GetAllDepartments',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    createdepartment: builder.mutation<ApiResponse<mstrdepartmententity>, mstrdepartmententity>({
      query: (mstrdepartmententity) => ({
        url: 'Department/CreateDepartment',
        method: 'POST',
        body: mstrdepartmententity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updatemstrdepartment: builder.mutation<ApiResponse<mstrdepartmententity>, mstrdepartmententity>({
      query: (mstrdepartmententity) => ({
        url: `Department/UpdateDepartment/${mstrdepartmententity.mstr_DepartmentID}`,
        method: 'PUT',
        body: mstrdepartmententity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),
    //Location/GetAllLocations   CreateLocation  UpdateLocation

    getallloacations: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'Location/GetAllLocations',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    createlocation: builder.mutation<ApiResponse<mstrlocationentity>, mstrlocationentity>({
      query: (mstrlocationentity) => ({
        url: 'Location/CreateLocation',
        method: 'POST',
        body: mstrlocationentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updatelocation: builder.mutation<ApiResponse<mstrlocationentity>, mstrlocationentity>({
      query: (mstrlocationentity) => ({
        url: `Location/UpdateLocation/${mstrlocationentity.mstr_LocationID}`,
        method: 'PUT',
        body: mstrlocationentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),
    //ShowPrefix/GetAllShowPrefixs   CreateShowPrefix  UpdateShowPrefix


    getallshowprefixs: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'ShowPrefix/GetAllShowPrefixs',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    createshowprefix: builder.mutation<ApiResponse<mstrshowprefixentity>, mstrshowprefixentity>({
      query: (mstrshowprefixentity) => ({
        url: 'ShowPrefix/CreateShowPrefix',
        method: 'POST',
        body: mstrshowprefixentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updateshowprefix: builder.mutation<ApiResponse<mstrshowprefixentity>, mstrshowprefixentity>({
      query: (mstrshowprefixentity) => ({
        url: `ShowPrefix/UpdateShowPrefix/${mstrshowprefixentity.mstr_SCPrefix_ID}`,
        method: 'PUT',
        body: mstrshowprefixentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    getAllTransportCodes: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'TransportCode/GetAllTransportCodes',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    createTransportCode: builder.mutation<ApiResponse<mstrtransportcodeentity>, mstrtransportcodeentity>({
      query: (mstrtransportcodeentity) => ({
        url: 'TransportCode/CreateTransportCode',
        method: 'POST',
        body: mstrtransportcodeentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updateTransportCode: builder.mutation<ApiResponse<mstrtransportcodeentity>, mstrtransportcodeentity>({
      query: (mstrtransportcodeentity) => ({
        url: `TransportCode/UpdateTransportCode/${mstrtransportcodeentity.mstr_TransportCode_Id}`,
        method: 'PUT',
        body: mstrtransportcodeentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),

    //Tax/GetAllTax CreateTax  UpdateTax   mstrvattaxentity


    getallvattaxs: builder.mutation<ApiResponse<BaseType>, BaseType>({
      query: (dto) => ({
        url: 'Tax/GetAllTax',
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    createvattax: builder.mutation<ApiResponse<mstrvattaxentity>, mstrvattaxentity>({
      query: (mstrvattaxentity) => ({
        url: 'Tax/CreateTax',
        method: 'POST',
        body: mstrvattaxentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),


    updatevattax: builder.mutation<ApiResponse<mstrvattaxentity>, mstrvattaxentity>({
      query: (mstrvattaxentity) => ({
        url: `Tax/UpdateTax/${mstrvattaxentity.mstr_VATTaxID}`,
        method: 'PUT',
        body: mstrvattaxentity,
      }),
      invalidatesTags: ['MastersLookups'],
    }),

    getDropdownList: builder.query<any, number>({
      query: (itemId) => ({
        url: `CommonLookup/GetDropdownList?itemId=${itemId}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => Array.isArray(response) ? response : [],
    }),

    getDmsAppConfigValue: builder.query<DmsAppConfigType, void>({
      query: () => ({
        url: 'CommonLookup/GetDmsAppConfigValue',
        method: 'GET',
      }),
      providesTags: ['MastersLookups']
    }),

    getEmailAppConfigValue: builder.query<EmailAppConfigType, void>({
      query: () => ({
        url: 'CommonLookup/GetEmailAppConfigValue',
        method: 'GET',
      }),
      providesTags: ['MastersLookups']
    }),

  }),
});

export const {
  useGetallchargecodesMutation,
  useCreatechargecodeMutation,
  useUpdatechargecodeMutation,
  useGetallcompanyMutation,
  useCreatemstrcomapanyMutation,
  useUpdatemstrcomapanyMutation,
  useGetalldepartmentMutation,
  useCreatedepartmentMutation,
  useUpdatemstrdepartmentMutation,
  useGetallloacationsMutation,
  useCreatelocationMutation,
  useUpdatelocationMutation,
  useGetallshowprefixsMutation,
  useCreateshowprefixMutation,
  useUpdateshowprefixMutation,
  useGetAllTransportCodesMutation,
  useCreateTransportCodeMutation,
  useUpdateTransportCodeMutation,
  useGetallvattaxsMutation,
  useCreatevattaxMutation,
  useUpdatevattaxMutation,
  useGetDropdownListQuery,
  useGetDmsAppConfigValueQuery,
  useGetEmailAppConfigValueQuery
  
} = mastersapi;